import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FolderOpen, Plus, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Project } from '@/types/project';
import { cn } from '@/lib/utils';

interface ProjectSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onCreateNew: () => void;
  canCreateProject?: boolean;
  onLimitReached?: () => void;
}

export function ProjectSelectionDialog({
  open,
  onOpenChange,
  projects,
  onSelectProject,
  onCreateNew,
  canCreateProject = true,
  onLimitReached,
}: ProjectSelectionDialogProps) {
  const handleCreateNewClick = () => {
    if (!canCreateProject) {
      onLimitReached?.();
    } else {
      onCreateNew();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Where do you want to add this rig?</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Existing Projects */}
          <ScrollArea className="max-h-[280px]">
            <div className="space-y-2 pr-3">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => onSelectProject(project)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:border-primary/50 hover:bg-accent/50 transition-colors text-left group overflow-hidden"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FolderOpen className="w-4 h-4 text-primary" />
                  </div>
                  <div className="w-0 flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{project.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {project.rigs.length} rig{project.rigs.length !== 1 ? 's' : ''} • Updated {formatDistanceToNow(new Date(project.updatedAt), { addSuffix: true })}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </button>
              ))}
            </div>
          </ScrollArea>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-background px-2 text-muted-foreground">or</span>
            </div>
          </div>

          {/* Create New Button */}
          <Button
            onClick={handleCreateNewClick}
            variant="outline"
            className={cn(
              "w-full justify-start gap-2",
              !canCreateProject && "opacity-50 cursor-not-allowed"
            )}
            disabled={!canCreateProject}
          >
            <Plus className="w-4 h-4" />
            Create New Project
            {!canCreateProject && (
              <span className="ml-auto text-xs text-muted-foreground">Limit reached</span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
